import {
  DEFAULT_SPECIAL_PRICE_RANGE,
  DEFAULT_SPECIAL_PRICE_STATUS_CONFIG,
  SPECIAL_PRICE_ATTRIBUTE_TITLES,
  FIRST_QUALIFIER_NUMBER,
  LAST_QUALIFIER_NUMBER,
  SPECIAL_PRICE_SCOPE_QUALIFIER_KEYS,
  SPECIAL_PRICE_STATUS_CONFIG
} from '../constant/constantspecial'

export const getStatusConfig = (status) => {
  return SPECIAL_PRICE_STATUS_CONFIG[status] || DEFAULT_SPECIAL_PRICE_STATUS_CONFIG
}

export const formatDateForDisplay = (value) => {
  if (!value) return '-'
  return value.toString().replace(/-/g, '/')
}

export const formatScopeLabelForDisplay = (value) => {
  if (!value) return '-'

  const scopeAttributeLabelPattern = new RegExp(
    `Atributo\\s+([${FIRST_QUALIFIER_NUMBER}-${LAST_QUALIFIER_NUMBER}])\\s*:`,
    'gi'
  )

  return String(value).replace(scopeAttributeLabelPattern, (_, attributeNumber) => {
    const attributeIndex = Number(attributeNumber) - FIRST_QUALIFIER_NUMBER
    const attributeLetter = SPECIAL_PRICE_ATTRIBUTE_TITLES[attributeIndex]?.split(' ').pop()

    return attributeLetter ? `Atributo ${attributeLetter}:` : `Atributo ${attributeNumber}:`
  })
}

export const getTodayISO = () => new Date().toISOString().split('T')[0]

export const createDefaultRange = () => ({
  ...DEFAULT_SPECIAL_PRICE_RANGE
})

export const buildAttributeDefinitions = (promoQualifiersPayload) => {
  const promoQualifiers = promoQualifiersPayload?.result?.[0] || {}

  return SPECIAL_PRICE_SCOPE_QUALIFIER_KEYS.map((key, index) => ({
    key,
    title: SPECIAL_PRICE_ATTRIBUTE_TITLES[index] || `Atributo ${index + 1}`,
    label: SPECIAL_PRICE_ATTRIBUTE_TITLES[index] || `Atributo ${index + 1}`,
    options: (promoQualifiers?.[key] || []).map((value) => ({ value, label: value }))
  }))
}

export const buildScopeFiltersPayload = ({
  attributeFilters,
  priceLists,
  attributeDefinitions
}) => {
  const selectedPriceList = priceLists.find(
    (priceList) => String(priceList.id) === String(attributeFilters.price_list_id)
  )

  const payload = {}

  if (selectedPriceList) {
    payload.price_list_id = selectedPriceList.id
    payload.price_list_db_ref = selectedPriceList.db_ref
    payload.price_list_name = selectedPriceList.description || selectedPriceList.db_ref
  }

  attributeDefinitions.forEach((definition) => {
    if (attributeFilters[definition.key]) {
      payload[definition.key] = [attributeFilters[definition.key]]
    }
  })

  return payload
}

const flattenMessages = (messages) => {
  if (!messages) return []
  if (typeof messages === 'string') return [messages]
  if (Array.isArray(messages)) return messages.flatMap(flattenMessages)
  if (typeof messages === 'object') return Object.values(messages).flatMap(flattenMessages)
  return []
}

export const extractErrorMessage = (payload, fallback = 'No se pudo procesar la solicitud.') => {
  const messages = [
    ...(payload?.message ? flattenMessages(payload.message) : []),
    ...(payload?.error ? flattenMessages(payload.error) : []),
    ...(payload?.errors ? flattenMessages(payload.errors) : [])
  ].filter(Boolean)

  return messages.length > 0 ? messages.join(' ') : fallback
}

export const getReactSelectStyles = (hasError = false) => ({
  control: (provided, state) => ({
    ...provided,
    borderColor: hasError ? '#dc3545' : state.isFocused ? '#86b7fe' : provided.borderColor,
    boxShadow: 'none',
    minHeight: '36px',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : '#86b7fe'
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 25
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1400
  })
})

export const getSpecialPriceInputError = (value) => {
  const specialPrice = String(value || '').trim()

  if (!specialPrice) return ''

  if (!/^\d+(\.\d{1,6})?$/.test(specialPrice)) {
    return 'El precio especial permite hasta 6 decimales.'
  }

  if (Number(specialPrice) <= 0) {
    return 'El precio especial debe ser mayor a cero.'
  }

  return ''
}

export const getRangeValidationMessage = (ranges) => {
  if (!ranges.length) return 'Debe agregar al menos una escala.'

  let expectedMin = 1

  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index]
    const minQty = Number(range.min_qty)
    const maxQty =
      range.max_qty === '' || range.max_qty === null || range.max_qty === undefined
        ? null
        : Number(range.max_qty)
    const specialPrice = String(range.special_price || '').trim()
    const specialPriceError = getSpecialPriceInputError(specialPrice)

    if (minQty !== expectedMin) {
      return 'Las escalas deben ser consecutivas y empezar en 1.'
    }

    if (!specialPrice) {
      return 'Debe ingresar un precio especial para cada escala.'
    }

    if (specialPriceError) {
      return specialPriceError
    }

    if (maxQty !== null) {
      if (!Number.isInteger(maxQty) || maxQty < minQty) {
        return 'El máximo de cada escala debe ser mayor o igual al mínimo.'
      }
      expectedMin = maxQty + 1
      continue
    }

    if (index !== ranges.length - 1) {
      return 'Solo la última escala puede ser ilimitada.'
    }
  }

  return ''
}

export const getDateValidationErrors = ({ startDate, endDate }) => {
  const errors = {}
  const today = getTodayISO()

  if (!startDate) {
    errors.start_date = 'Debe seleccionar una fecha inicial.'
  } else if (startDate < today) {
    errors.start_date = 'No puede escoger fechas pasadas.'
  }

  if (!endDate) {
    errors.end_date = 'Debe seleccionar una fecha final.'
  } else if (endDate < today) {
    errors.end_date = 'No puede escoger fechas pasadas.'
  }

  if (startDate && endDate && endDate < startDate) {
    errors.end_date = 'La fecha final no puede ser menor a la fecha inicial.'
  }

  return errors
}
